import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Calendar, Plus, Edit, Trash2, Loader2, Upload, MapPin, Link as LinkIcon,
  Image as ImageIcon, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { PresignedImage } from '../components/PresignedMedia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  description: '',
  event_date: '',
  event_time: '',
  location_address: '',
  location_lat: '',
  location_lng: '',
  image_url: '',
  social_link: '',
};

export default function AdminEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events?upcoming_only=false&limit=100`);
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    setForm({
      name: event.name || '',
      description: event.description || '',
      event_date: event.event_date || '',
      event_time: event.event_time || '',
      location_address: event.location_address || '',
      location_lat: event.location_lat != null ? String(event.location_lat) : '',
      location_lng: event.location_lng != null ? String(event.location_lng) : '',
      image_url: event.image_url || '',
      social_link: event.social_link || '',
    });
    setModalOpen(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG/PNG/WebP allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Max file size is 10MB');
      return;
    }
    setUploadingImage(true);
    try {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const targetId = editing?.id || 'new';
      const presignRes = await axios.post(`${API}/presigned-url/upload`, {
        file_type: 'event',
        content_type: file.type,
        file_extension: extension,
        target_id: targetId,
      });
      const { upload_url, file_path } = presignRes.data;
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type },
        transformRequest: [(data, headers) => {
          delete headers.Authorization; delete headers.common; return data;
        }],
      });
      setForm(f => ({ ...f, image_url: file_path }));
      toast.success('Image uploaded');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.event_date) { toast.error('Date is required'); return; }

    const payload = {
      name: form.name.trim(),
      description: form.description || null,
      event_date: form.event_date,
      event_time: form.event_time || null,
      location_address: form.location_address || null,
      location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
      location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
      image_url: form.image_url || null,
      social_link: form.social_link || null,
    };

    try {
      if (editing) {
        await axios.put(`${API}/events/${editing.id}`, payload);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success('Event created');
      }
      setModalOpen(false);
      setEditing(null);
      setForm(emptyForm);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const now = new Date();
  const upcoming = events.filter(e => new Date(e.event_date) >= now);
  const past = events.filter(e => new Date(e.event_date) < now);

  return (
    <div className="space-y-6" data-testid="admin-events-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Events</h1>
        <Button onClick={openCreate} className="bg-red-500 hover:bg-red-600" data-testid="create-event-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-[#121212] rounded-2xl border border-white/10">
          <Calendar className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400">No events yet. Create your first one!</p>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">Upcoming</h2>
              {upcoming.map(event => (
                <EventRow key={event.id} event={event} onEdit={openEdit} onDelete={handleDelete} />
              ))}
            </section>
          )}
          {past.length > 0 && (
            <section className="space-y-3 opacity-70">
              <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">Past</h2>
              {past.map(event => (
                <EventRow key={event.id} event={event} onEdit={openEdit} onDelete={handleDelete} />
              ))}
            </section>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? 'Edit Event' : 'Create Event'}</DialogTitle>
            <DialogDescription className="text-neutral-400">
              {editing ? 'Update event details' : 'Add a new meet, cruise, or show'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white">Event Name*</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Summer Cruise Meet"
                className="bg-neutral-900 border-white/10 text-white mt-1"
                data-testid="event-name-input"
              />
            </div>

            <div>
              <Label className="text-white">Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What's happening..."
                className="bg-neutral-900 border-white/10 text-white mt-1"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Date*</Label>
                <Input
                  type="date"
                  value={form.event_date}
                  onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                  className="bg-neutral-900 border-white/10 text-white mt-1"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label className="text-white">Time</Label>
                <Input
                  type="time"
                  value={form.event_time}
                  onChange={e => setForm(f => ({ ...f, event_time: e.target.value }))}
                  className="bg-neutral-900 border-white/10 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-white flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Location Address
              </Label>
              <Input
                value={form.location_address}
                onChange={e => setForm(f => ({ ...f, location_address: e.target.value }))}
                placeholder="123 Main St, Vancouver, BC"
                className="bg-neutral-900 border-white/10 text-white mt-1"
              />
              <p className="text-xs text-neutral-500 mt-1">Will be clickable to Google Maps on the site</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Latitude (optional)</Label>
                <Input
                  value={form.location_lat}
                  onChange={e => setForm(f => ({ ...f, location_lat: e.target.value }))}
                  placeholder="49.2827"
                  className="bg-neutral-900 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Longitude (optional)</Label>
                <Input
                  value={form.location_lng}
                  onChange={e => setForm(f => ({ ...f, location_lng: e.target.value }))}
                  placeholder="-123.1207"
                  className="bg-neutral-900 border-white/10 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-white flex items-center gap-1">
                <LinkIcon className="w-3.5 h-3.5" /> Social Post Link
              </Label>
              <Input
                value={form.social_link}
                onChange={e => setForm(f => ({ ...f, social_link: e.target.value }))}
                placeholder="https://instagram.com/p/..."
                className="bg-neutral-900 border-white/10 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-white flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" /> Event Image
              </Label>
              {form.image_url ? (
                <div className="relative mt-2 rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/10">
                  <PresignedImage src={form.image_url} alt="Event" className="w-full h-40 object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex items-center justify-center gap-2 p-6 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-red-500/50 hover:bg-white/5 transition-all">
                  {uploadingImage ? (
                    <><Loader2 className="w-5 h-5 animate-spin text-red-400" /><span className="text-sm text-neutral-400">Uploading...</span></>
                  ) : (
                    <><Upload className="w-5 h-5 text-neutral-400" /><span className="text-sm text-neutral-400">Click to upload (JPEG/PNG/WebP)</span></>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e.target.files?.[0])}
                    disabled={uploadingImage}
                    data-testid="event-image-upload"
                  />
                </label>
              )}
            </div>

            <Button
              onClick={handleSave}
              className="w-full bg-red-500 hover:bg-red-600 rounded-full"
              disabled={uploadingImage}
              data-testid="save-event-btn"
            >
              {editing ? 'Update Event' : 'Create Event'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventRow({ event, onEdit, onDelete }) {
  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };
  return (
    <div className="bg-[#121212] rounded-2xl border border-white/10 p-4 flex items-start gap-4" data-testid={`event-row-${event.id}`}>
      <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/10 flex-shrink-0">
        {event.image_url ? (
          <PresignedImage src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-600">
            <Calendar className="w-6 h-6" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            {formatDate(event.event_date)}{event.event_time ? ` · ${event.event_time}` : ''}
          </Badge>
        </div>
        <h3 className="text-white font-semibold truncate">{event.name}</h3>
        {event.location_address && (
          <p className="text-sm text-neutral-400 truncate flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {event.location_address}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(event)} className="border-white/20 text-neutral-400">
          <Edit className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => onDelete(event.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
